import React, { ReactElement, useState } from 'react';
import OrderHistoryHeader from './OrderHistoryHeader';
import { ItemGroupSummary, PurchaseHistory } from "../component";
import OlderOrdersComponent from './OlderOrdersComponent';
import OrderHistoryRedirectionComponentRow from './OrderHistoryRedirectionComponent'
import DispenseStatementBottomSheet from "./DispenseStatementBottomSheet";
import { CategoryTabs } from "../common/CategoryTabs";
import { AboveTheFoldMarker, CriticalFeatureMarker, FunctionalMarker } from "../csa/markers";
import DeliveryCard from "../common/DeliveryCard";
import SeeFuturePillPackDeliveriesCard from './SeeFuturePillPackDeliveriesCard';
import { CsaTagProps, generatePastDeliveryCardCsaTag, generateUpcomingDeliveryCardCsaTag } from "../csa/utils";

interface OrderHistoryProps {
    purchaseHistory: PurchaseHistory;
    getOrderHistory: (since: Date) => any;
    orderHistoryRedirectionComponentRow ?: ReactElement;
}

function OrderHistory(props: OrderHistoryProps) {
    const categories = [props.purchaseHistory.labels.upcomingOrdersTab, props.purchaseHistory.labels.pastOrdersTab];
    const { upcoming } = props.purchaseHistory;
    const [selectedCategory, setSelectedCategory] = useState<number>(0);

    const _generateUpcomingItemGroupSummary = () => {
        return props.purchaseHistory.upcoming.map((summary, index) => {
            return _processItemGroups(summary, generateUpcomingDeliveryCardCsaTag(index, summary))
        });
    }

    /**
     * Generate Card to See Future PillPack Schedule
     *
     */
    const _generateSeeFuturePillPackDeliveriesCard = () => {
        const pillPackDeliverySchedulePage = props.purchaseHistory.pillPackDeliveryScheduleCard;
        const enrolledPillPack = pillPackDeliverySchedulePage.isUserEnrolledForPillPack;
        if (!enrolledPillPack) {
            return null;
        };
        const headLine = pillPackDeliverySchedulePage.seeFuturePillPackDeliveries;
        if (!headLine) {
            return null;
        }
        return <SeeFuturePillPackDeliveriesCard label={headLine} />
    }
    const _generatePastItemGroupSummary = () => {
        const previousOrders = [...props.purchaseHistory.past]

        if(previousOrders.length === 0) {
            return null;
        } else {
            return (<>
                {previousOrders.length > 0 && <>
                    {previousOrders.map((summary, index) =>
                      _processItemGroups(summary, generatePastDeliveryCardCsaTag(index, summary)))}
                </>
                }
            </>);
        }
    }

    const _showPillPackOrderHistory = () => {
        if (props.purchaseHistory.isMigratedCustomer) {
            return (<> {
                <pui-section
                    id="purchase-history-migrated-customer-section"
                    flowDirection="horizontal"
                    secondaryAxisArrangement="center"
                    spacingTop="tiny"
                    spacingBottom="medium"
                >
                    <pui-text input={props.purchaseHistory.labels.orderHistory} spacingTop="tiny"
                              spacingRight="mini"></pui-text>

                    <DispenseStatementBottomSheet
                        spaceTopCssProps="tiny"
                        getMedicationOrderStatements={props.purchaseHistory.labels.orderStatement}
                        iosDevice={props.purchaseHistory.iosDevice}
                        androidDevice={props.purchaseHistory.androidDevice}
                        mShop={props.purchaseHistory.mShop}/>
                </pui-section>
            }</>);
        }
    }

    const _processItemGroups = (itemGroupSummary: ItemGroupSummary, csaTagProps: CsaTagProps) => {
        return (
            <DeliveryCard
                key={itemGroupSummary.id}
                itemGroupSummary={itemGroupSummary}
                trackingLinkLabel={props.purchaseHistory.labels.trackDelivery}
                orderDetailsLinkLabel={props.purchaseHistory.labels.seeDetails}
                pickUpInstructionsLabel={props.purchaseHistory.labels.pickUpInstructions}
                bypassTracker={itemGroupSummary.bypassTracker}
                csaTagProps={csaTagProps}
            />);
    }

    const _generateHeader= () => {
        return (
            <pui-stack>
                {props.orderHistoryRedirectionComponentRow?props.orderHistoryRedirectionComponentRow:<OrderHistoryRedirectionComponentRow isRedirected={false}/>}
                <OrderHistoryHeader headline = {props.purchaseHistory.labels.yourOrders} helpLink={props.purchaseHistory.labels.helpLink}/>
                {
                    <DispenseStatementBottomSheet
                        getMedicationOrderStatements={props.purchaseHistory.labels.getMedicationOrderStatements}
                        iosDevice={props.purchaseHistory.iosDevice}
                        androidDevice={props.purchaseHistory.androidDevice}
                        mShop={props.purchaseHistory.mShop}/>
                }
            </pui-stack>
        )
    }

    return (
        <pui-stack id="order-history-page" data-csa-c-content-id="order-history-page" data-csa-c-type="card" data-csa-c-slot-id="order-history">
            {_generateHeader()}
            <pui-stack-item spacingTop="mini" spacingBottom="med-small">
                <CategoryTabs onChange={setSelectedCategory} selectedCategory={selectedCategory} categories={categories}/>
                <AboveTheFoldMarker/>
                <>
                    <pui-divider class="mobile-only" style={{ height: '1px', width: '100vw', marginLeft: '-20px', position: 'relative' }} />
                    <pui-divider class="tablet-and-desktop-only " style={{ height: '1px', width: '100%', position: 'relative' }} />
                </>
            </pui-stack-item>
            {(!upcoming || upcoming.length === 0) &&
                <pui-stack
                    direction="vertical"
                    secondaryAxisArrangement="center"
                    spacingTop="mini"
                    spacingBottom="medium" style={{ width: '100%', display: selectedCategory === 0 ? 'block' : 'none' }}>
                    <pui-stack-item>
                        <pui-text input={props.purchaseHistory.labels.noUpcomingOrders} spacingTop="medium" spacingBottom="medium" />
                    </pui-stack-item>
                </pui-stack>}
            <div style={{display: selectedCategory === 0 ? 'block' : 'none'}}>
                {_generateUpcomingItemGroupSummary()}
                {_generateSeeFuturePillPackDeliveriesCard()}
            </div>
            <CriticalFeatureMarker/>
            <div style={{display: selectedCategory === 1 ? 'block' : 'none'}}>
                {_showPillPackOrderHistory()}
                {_generatePastItemGroupSummary()}
            <FunctionalMarker/>
            <OlderOrdersComponent
                getOrderHistory={props.getOrderHistory}
                _processItemGroups={_processItemGroups}
                purchaseHistory={props.purchaseHistory}
                listDelimiter={<></>}
                />
            </div>
        </pui-stack>
    );
}

export default OrderHistory;
